import { apiClient } from './configs/axios-setup'

export type SolveMetricKey =
  | 'average-solve-time'
  | 'iteration-duration'
  | 'average-iterations-to-solve'

export interface SolveMetricTeamScope {
  viewAllTeams: boolean
  teams: string[]
}

export interface SolveMetricStatisticsRequest {
  teamScope: SolveMetricTeamScope
  dateRanges: Record<SolveMetricKey, string>
}

export interface SolveMetricStatistic {
  key: SolveMetricKey
  hematiteValue: string
  raspiteValue: string
  natroliteValue: string
  iolite116CoreValue: string
}

export interface SolveMetricStatisticsResponse {
  statistics: SolveMetricStatistic[]
}

export interface SolveMetricDetailRequest {
  statKey: SolveMetricKey
  dateRange: string
  chartYear: string
  teamScope: SolveMetricTeamScope
}

export interface SolveMetricRunTableRequest {
  teamScope: SolveMetricTeamScope
}

export interface SolveMetricChartDatum {
  month: string
  hematite: number
  raspite: number
  natrolite: number
  iolite116Core: number
}

export type SolveMetricTableRow = Record<string, string | number | null>

export interface SolveMetricRunTableRow {
  run: string | null
  coreType: string | null
  cellsPerStage: number | string | null
  startTime: string | null
  sumProcessors: number | string | null
  solveTimeH: number | string | null
  wallTimeH: number | string | null
  sumCpuHours: number | string | null
  avgIterationDuration: number | string | null
  finalIterationAmount: number | string | null
  hpcAsset: string | null
  solutionSoftware: string | null
  user: string | null
}

export interface SolveMetricDetailResponse {
  chartData: SolveMetricChartDatum[]
  tableRows: SolveMetricTableRow[]
}

export interface SolveMetricRunTableResponse {
  tableRows: SolveMetricRunTableRow[]
}

const solveMetricKeys: SolveMetricKey[] = [
  'average-solve-time',
  'iteration-duration',
  'average-iterations-to-solve'
]
const solveMetricMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]
const useSolveMetricsApi = process.env.REACT_APP_USE_SOLVE_METRICS_API === 'true'

const buildStubChartData = (chartYear: string): SolveMetricChartDatum[] => {
  const months =
    chartYear === 'ytd' ? solveMetricMonths.slice(0, new Date().getMonth() + 1) : solveMetricMonths

  return months.map(month => ({
    month,
    hematite: 0,
    raspite: 0,
    natrolite: 0,
    iolite116Core: 0
  }))
}

export const getSolveMetricStatistics = async (
  request: SolveMetricStatisticsRequest
): Promise<SolveMetricStatisticsResponse> => {
  if (useSolveMetricsApi) {
    // Example payload:
    // {
    //   "teamScope": {
    //     "viewAllTeams": false,
    //     "teams": ["Hendrick Motorsports", "Trackhouse Racing"]
    //   },
    //   "dateRanges": {
    //     "average-solve-time": "this-week",
    //     "iteration-duration": "past-3-months",
    //     "average-iterations-to-solve": "all-time"
    //   }
    // }
    const { data } = await apiClient.post<SolveMetricStatisticsResponse>(
      '/SolveMetrics/statistics',
      request
    )
    return data
  }

  return {
    statistics: solveMetricKeys.map(key => ({
      key,
      hematiteValue: '--',
      raspiteValue: '--',
      natroliteValue: '--',
      iolite116CoreValue: '--'
    }))
  }
}

export const getSolveMetricDetail = async (
  request: SolveMetricDetailRequest
): Promise<SolveMetricDetailResponse> => {
  if (useSolveMetricsApi) {
    // Example payload:
    // {
    //   "statKey": "iteration-duration",
    //   "dateRange": "past-3-months",
    //   "chartYear": "ytd",
    //   "teamScope": {
    //     "viewAllTeams": true,
    //     "teams": []
    //   }
    // }
    const detailEndpointByStatKey: Record<SolveMetricKey, string> = {
      'average-solve-time': '/SolveMetrics/AvgSolveTime',
      'iteration-duration': '/SolveMetrics/IterationDuration',
      'average-iterations-to-solve': '/SolveMetrics/AvgIterationsToSolve'
    }
    const { data } = await apiClient.post<SolveMetricDetailResponse>(
      detailEndpointByStatKey[request.statKey],
      request
    )
    return data
  }

  return {
    chartData: buildStubChartData(request.chartYear),
    tableRows: []
  }
}

export const getSolveMetricRunTable = async (
  request: SolveMetricRunTableRequest
): Promise<SolveMetricRunTableResponse> => {
  if (useSolveMetricsApi) {
    // Example payload:
    // {
    //   "teamScope": {
    //     "viewAllTeams": false,
    //     "teams": ["Hendrick Motorsports", "Trackhouse Racing"]
    //   }
    // }
    const { data } = await apiClient.post<SolveMetricRunTableResponse>(
      '/SolveMetrics/RunTable',
      request
    )
    return data
  }

  return {
    tableRows: []
  }
}
